import { apiClient } from '../client';
import type { paths, components } from '../schema.gen';

export type SystemRiskResponse = components['schemas']['SystemRiskResponse'];
export type ConfirmationTokenResponse = components['schemas']['ConfirmationTokenResponse'];
export type ExchangeListResponse = components['schemas']['ExchangeListResponse'];
export type ExchangeMaintenanceResponse = components['schemas']['ExchangeMaintenanceResponse'];

type SystemHealthResponse =
  paths['/api/v1/system/health']['get']['responses']['200']['content']['application/json'];

type SystemMetricsResponse =
  paths['/api/v1/system/metrics']['get']['responses']['200']['content']['application/json'];

type ExchangesHealthResponse =
  paths['/api/v1/system/exchanges/health']['get']['responses']['200']['content']['application/json'];

type DataQualityResponse =
  paths['/api/v1/system/data-quality']['get']['responses']['200']['content']['application/json'];

export async function getSystemHealth(): Promise<SystemHealthResponse> {
  const { data } = await apiClient.get<SystemHealthResponse>('/api/v1/system/health');
  return data;
}

export async function getSystemMetrics(): Promise<SystemMetricsResponse> {
  const { data } = await apiClient.get<SystemMetricsResponse>('/api/v1/system/metrics');
  return data;
}

export async function getExchangesHealth(): Promise<ExchangesHealthResponse> {
  const { data } = await apiClient.get<ExchangesHealthResponse>('/api/v1/system/exchanges/health');
  return data;
}

export async function getDataQuality(): Promise<DataQualityResponse> {
  const { data } = await apiClient.get<DataQualityResponse>('/api/v1/system/data-quality');
  return data;
}

export async function getSystemRisk(): Promise<SystemRiskResponse> {
  const { data } = await apiClient.get<SystemRiskResponse>('/api/v1/system/risk');
  return data;
}

export async function getConfirmationToken(scope: string): Promise<ConfirmationTokenResponse> {
  const { data } = await apiClient.post<ConfirmationTokenResponse>(
    '/api/v1/system/confirmation-token',
    null,
    { params: { scope } },
  );
  return data;
}

export async function systemHalt(reason: string): Promise<void> {
  await apiClient.post('/api/v1/system/halt', { reason });
}

export async function systemResume(confirmation_token: string): Promise<void> {
  await apiClient.post('/api/v1/system/resume', { confirmation_token });
}

export async function getExchanges(): Promise<ExchangeListResponse> {
  const { data } = await apiClient.get<ExchangeListResponse>('/api/v1/system/exchanges');
  return data;
}

export async function getExchangesMaintenance(): Promise<ExchangeMaintenanceResponse> {
  const { data } = await apiClient.get<ExchangeMaintenanceResponse>(
    '/api/v1/system/exchanges/maintenance',
  );
  return data;
}

// ── Kill Switch (multi-scope) ────────────────────────────────────────────────

// Types not yet in schema.gen.ts — defined inline
export type KillSwitchStatusResponse = {
  active: boolean;
  scope: string;
  activated_at: string | null;
  activated_by: string | null;
  reason: string | null;
};

export type KillSwitchActivateRequest = {
  reason: string;
  scope?: string;
};

/** GET /system/kill-switch — global kill switch status */
export async function getKillSwitchStatus(): Promise<KillSwitchStatusResponse> {
  const { data } = await apiClient.get<KillSwitchStatusResponse>('/api/v1/system/kill-switch');
  return data;
}

/** POST /system/kill-switch — activate global kill switch (ADMIN) */
export async function activateKillSwitch(body: KillSwitchActivateRequest): Promise<void> {
  await apiClient.post('/api/v1/system/kill-switch', body);
}

/** DELETE /system/kill-switch — deactivate global kill switch (ADMIN + confirmation token) */
export async function deactivateKillSwitch(confirmation_token: string): Promise<void> {
  await apiClient.delete('/api/v1/system/kill-switch', {
    data: { confirmation_token },
  });
}

/** GET /system/kill-switch/user/{userId} — per-user kill switch status */
export async function getUserKillSwitchStatus(userId: string): Promise<KillSwitchStatusResponse> {
  const { data } = await apiClient.get<KillSwitchStatusResponse>(
    `/api/v1/system/kill-switch/user/${userId}`,
  );
  return data;
}

/** POST /system/kill-switch/user/{userId} — activate per-user kill switch */
export async function activateUserKillSwitch(
  userId: string,
  body: KillSwitchActivateRequest,
): Promise<void> {
  await apiClient.post(`/api/v1/system/kill-switch/user/${userId}`, body);
}

/** DELETE /system/kill-switch/user/{userId} — deactivate per-user kill switch */
export async function deactivateUserKillSwitch(
  userId: string,
  confirmation_token: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/system/kill-switch/user/${userId}`, {
    data: { confirmation_token },
  });
}

// ── Exchange Circuit Breaker ─────────────────────────────────────────────────

// Types not yet in schema.gen.ts — defined inline
export type ExchangeCBListResponse = {
  data: ExchangeCBDetailResponse[];
  total: number;
};

export type ExchangeCBDetailResponse = {
  id: string;
  exchange: string;
  status: string;
  tripped_at: string | null;
  recovered_at: string | null;
  failure_count: number;
  details: Record<string, unknown>;
};

/** GET /system/exchange-cb — list exchange circuit breaker states */
export async function getExchangeCBList(): Promise<ExchangeCBListResponse> {
  const { data } = await apiClient.get<ExchangeCBListResponse>('/api/v1/system/exchange-cb');
  return data;
}

/** GET /system/exchange-cb/{id} — get specific exchange CB detail */
export async function getExchangeCBDetail(id: string): Promise<ExchangeCBDetailResponse> {
  const { data } = await apiClient.get<ExchangeCBDetailResponse>(
    `/api/v1/system/exchange-cb/${id}`,
  );
  return data;
}

/** POST /system/exchange-cb/{id}/recover — recover from CB trip (ADMIN + confirmation token) */
export async function recoverExchangeCB(id: string, confirmation_token: string): Promise<void> {
  await apiClient.post(`/api/v1/system/exchange-cb/${id}/recover`, { confirmation_token });
}
